/**
 * <hy-product-nav>: progressive enhancement for sections/hy-product-nav.liquid
 * (AP-250, PR6). The bar works without this module (plain anchors, sticky via
 * CSS). It adds:
 * - aria-current="true" on the link whose section is in view,
 * - [data-stuck] while the sticky bar is stuck (sentinel before the wrapper),
 *   and [data-hy-nav-ready] once that is tracked (summary shows only stuck),
 * - --hy-product-nav-header-offset: height of the compact sticky header,
 * - --hy-product-nav-scroll-offset on :root: measured bar height for
 *   scroll-padding, so jump targets do not land under the bar,
 * - the summary price mirrored from the main price on hy:variant:changed.
 */
// The "in view" band: 20% to 40% of the viewport height from the top.
const ACTIVE_BAND = '-20% 0px -60% 0px';
const ACTIVE_BAND_BOTTOM = 0.4;
// How far below the viewport the stuck observer's root extends (px): taller
// than any page, so a sentinel below the fold still counts as "in view".
const STUCK_ROOT_EXTENSION = 100000;

class HyProductNav extends HTMLElement {
  #observers = [];
  #controller = null;
  #sentinel = null;

  connectedCallback() {
    this.#controller = new AbortController();
    this.#hideMissingTargets();
    this.#trackActive();
    if (this.classList.contains('hy-product-nav--sticky')) this.#trackSticky();
    if (this.querySelector('.hy-product-nav__price')) {
      document.addEventListener('hy:variant:changed', () => this.#syncPrice(), {
        signal: this.#controller.signal,
      });
    }
  }

  disconnectedCallback() {
    this.#observers.forEach((observer) => observer.disconnect());
    this.#observers = [];
    this.#controller?.abort();
    this.#controller = null;
    this.#sentinel?.remove();
    this.#sentinel = null;
    document.documentElement.style.removeProperty('--hy-product-nav-scroll-offset');
  }

  #observe(observer, ...targets) {
    targets.forEach((target) => observer.observe(target));
    this.#observers.push(observer);
  }

  // A section that renders nothing for this product (e.g. no accessories)
  // leaves its jump link without a target; hide the link instead of offering
  // a jump that goes nowhere. Without JavaScript the link stays, harmlessly.
  #hideMissingTargets() {
    this.querySelectorAll('.hy-product-nav__link').forEach((link) => {
      const id = link.hash.slice(1);
      if (id && !document.getElementById(id)) link.closest('li')?.setAttribute('hidden', '');
    });
  }

  #trackActive() {
    const links = [...this.querySelectorAll('.hy-product-nav__link')];
    const pairs = links
      .map((link) => [link, document.getElementById(link.hash.slice(1))])
      .filter(([, target]) => target);
    if (pairs.length === 0) return;

    const visible = new Set();
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) visible.add(entry.target);
          else visible.delete(entry.target);
        });
        // The last section in the band wins: at a boundary the outgoing
        // section's bottom padding is still in view, the incoming one is
        // what the reader just scrolled to.
        const active = pairs.findLast(([, target]) => visible.has(target));
        // Between two sections the last marker stays; above the first
        // target (back at the top of the page) it is cleared.
        const aboveFirst = pairs[0][1].getBoundingClientRect().top > window.innerHeight * ACTIVE_BAND_BOTTOM;
        if (!active && !aboveFirst) return;
        links.forEach((link) => link.removeAttribute('aria-current'));
        if (active) active[0].setAttribute('aria-current', 'true');
      },
      { rootMargin: ACTIVE_BAND },
    );
    this.#observe(observer, ...pairs.map(([, target]) => target));
  }

  #trackSticky() {
    const host = this.closest('.shopify-section') ?? this;

    this.#sentinel = document.createElement('div');
    this.#sentinel.setAttribute('aria-hidden', 'true');
    host.before(this.#sentinel);
    // The root reaches far below the viewport, so only crossing the TOP edge
    // changes the state. With the plain viewport a jump link (or a fast
    // scroll) from above the bar to below it never let the sentinel pass
    // through the viewport: no intersection change, no callback, and the
    // bar stayed "not stuck" (design review round 2; same trap as in
    // assets/hy-sticky-atc.js).
    this.#observe(
      new IntersectionObserver(
        ([entry]) => {
          const stuck = !entry.isIntersecting && entry.boundingClientRect.top < 0;
          this.toggleAttribute('data-stuck', stuck);
        },
        { rootMargin: `0px 0px ${STUCK_ROOT_EXTENSION}px 0px` },
      ),
      this.#sentinel,
    );
    // From here on [data-stuck] is tracked, so the CSS may hide the summary
    // while the bar is still in the flow.
    this.setAttribute('data-hy-nav-ready', '');

    this.#observe(
      new ResizeObserver(() => {
        document.documentElement.style.setProperty(
          '--hy-product-nav-scroll-offset',
          `${this.offsetHeight}px`,
        );
      }),
      this,
    );

    const header = document.querySelector('hy-sticky-header');
    if (!header) return;
    this.#observe(
      new ResizeObserver(() => {
        host.style.setProperty('--hy-product-nav-header-offset', `${header.offsetHeight}px`);
      }),
      header,
    );
  }

  #syncPrice() {
    const value = document.querySelector('[data-hy-price] .hy-main-product__price-value');
    const price = this.querySelector('.hy-product-nav__price');
    if (value && price) price.textContent = value.textContent;
  }
}

if (!customElements.get('hy-product-nav')) {
  customElements.define('hy-product-nav', HyProductNav);
}
